// DOM overlay helpers. Each phase mounts one or more overlays into #app.
// Always tear down in phase.exit() to keep the DOM clean.

export function mount(root: HTMLElement, el: HTMLElement): () => void {
  root.appendChild(el);
  return () => {
    if (el.parentElement === root) root.removeChild(el);
  };
}

export function clearChildren(el: HTMLElement): void {
  while (el.firstChild) el.removeChild(el.firstChild);
}

// Reference logical width (px) of the in-frame UI. All fixed-px sizing inside
// `.app-frame` is authored against this width; the frame is transform-scaled to
// fill its variable-size host, so the whole UI scales as one unit with the
// frame. ~405 keeps typical phones near scale 1 (current look preserved) while
// scaling up on wide desktop frames.
const FRAME_REF_W = 405;

/** Mount a phase's `.app-frame` content inside a variable-size `.app-frame-host`
 *  and keep it transform-scaled to fill the host. The content is laid out in a
 *  fixed FRAME_REF_W-wide logical space (height derived from the host's aspect)
 *  and scaled by host-width / FRAME_REF_W — width-driven, so tall screens stay
 *  full-bleed (no letterboxing). Returns a teardown fn (disconnects the
 *  observer + removes the host). See index.html `.app-frame` / `.app-frame-host`. */
export function mountFrame(root: HTMLElement, frame: HTMLElement): () => void {
  const host = document.createElement("div");
  host.className = "app-frame-host";
  host.appendChild(frame);
  root.appendChild(host);

  const apply = () => {
    const w = host.clientWidth;
    const h = host.clientHeight;
    if (!w || !h) return;
    const scale = w / FRAME_REF_W;
    frame.style.setProperty("--frame-w", `${FRAME_REF_W}px`);
    frame.style.setProperty("--frame-h", `${h / scale}px`); // scaled height == host height
    frame.style.setProperty("--frame-scale", `${scale}`);
  };
  apply();
  const ro = new ResizeObserver(apply);
  ro.observe(host);

  return () => {
    ro.disconnect();
    if (host.parentElement === root) root.removeChild(host);
  };
}
